import { ref } from 'vue'
import { supabase, authHeader, type OrgSettings } from '../supabase'

// Module-level singleton — shared across all importers.
const org = ref<OrgSettings | null>(null)
const currentRole = ref<string | null>(null)
let loadPromise: Promise<OrgSettings | null> | null = null

/** Role of the active user in their active org. Loaded by App.vue via fetchMyOrgs. */
export function setCurrentRole(role: string | null) { currentRole.value = role }
export function getCurrentRole(): string | null { return currentRole.value }
export function isTechnician(): boolean { return currentRole.value === 'technician' }

async function getActiveOrgIdFromSession(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  let id = (data.session?.user as any)?.app_metadata?.active_org_id
  if (!id && data.session) {
    // Stale JWT predates multi-tenant migration; refresh to mint a token carrying the claim.
    await supabase.auth.refreshSession()
    const { data: d2 } = await supabase.auth.getSession()
    id = (d2.session?.user as any)?.app_metadata?.active_org_id
  }
  return id || null
}

/** Synchronous helper for insert payloads. Always prefer the JWT claim — that's the
 *  exact value RLS compares against. `org.value` can drift if loadOrgSettings() fell
 *  back to a limit(1) row that doesn't match the active claim, so it's last resort. */
export function currentOrgId(): string | null {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'))
    for (const k of keys) {
      const raw = localStorage.getItem(k)
      if (!raw) continue
      const parsed = JSON.parse(raw)
      const id = parsed?.user?.app_metadata?.active_org_id
        ?? parsed?.currentSession?.user?.app_metadata?.active_org_id
      if (id) return id
    }
  } catch { /* noop */ }
  return org.value?.id ?? null
}

export async function loadOrgSettings(force = false): Promise<OrgSettings | null> {
  if (!force && org.value) return org.value
  if (!force && loadPromise) return loadPromise
  loadPromise = (async () => {
    const activeId = await getActiveOrgIdFromSession()
    let row: OrgSettings | null = null
    if (activeId) {
      const { data } = await supabase.from('org_settings').select('*').eq('id', activeId).maybeSingle()
      row = (data as OrgSettings | null) ?? null
    }
    if (!row) {
      const { data } = await supabase.from('org_settings').select('*').limit(1).maybeSingle()
      row = (data as OrgSettings | null) ?? null
    }
    org.value = row
    return org.value
  })()
  try {
    return await loadPromise
  } finally {
    loadPromise = null
  }
}

export async function refreshOrgSettings(): Promise<OrgSettings | null> {
  return loadOrgSettings(true)
}

export async function switchOrg(orgId: string): Promise<void> {
  const hdrs = await authHeader()
  const res = await fetch('/api/me/active-org', {
    method: 'POST',
    headers: { ...hdrs, 'content-type': 'application/json' },
    body: JSON.stringify({ org_id: orgId })
  })
  if (!res.ok) throw new Error(await res.text())
  await supabase.auth.refreshSession()
  await refreshOrgSettings()
}

export async function createOrg(payload: {
  name: string; legal_name?: string; slug?: string; address?: string
  gst_registered?: boolean; gst_rate?: number; default_currency?: string
}): Promise<{ id: string }> {
  const hdrs = await authHeader()
  const res = await fetch('/api/orgs', {
    method: 'POST',
    headers: { ...hdrs, 'content-type': 'application/json' },
    body: JSON.stringify(payload)
  })
  if (!res.ok) throw new Error(await res.text())
  const j = await res.json()
  await supabase.auth.refreshSession()
  await refreshOrgSettings()
  return j.org
}

export function useOrgSettings() {
  return { org, loadOrgSettings, refreshOrgSettings, switchOrg, createOrg, currentOrgId, currentRole, isTechnician }
}
